import { useState, type FormEvent } from 'react';

import { apiUrl, type CatalogueCard, type Filters, type ShowAnswer } from '../api';
import { availabilityText, Back, Failed, Spinner, StockCheck, Thumb, Top, useCall } from '../components/parts';
import { Link, navigate } from '../router';
import { cards } from '../words';

// One show (José's picks, 23 Oct 2026): its floor plan with every table, its shops, and the cards they bring, with
// search and filters. Tapping a shop's table lights it on the plan.

const FILTER_KEYS = ['q', 'slabs', 'grader', 'grade', 'lang', 'set', 'min', 'max'] as const;

export function ShowPage({ slug, query }: { slug: string; query: URLSearchParams }) {
  const qs = query.toString();
  const { data, error, loading, reload } = useCall<ShowAnswer>(`/api/find/shows/${encodeURIComponent(slug)}${qs ? `?${qs}` : ''}`);

  if (error && !data)
    return (
      <>
        <Top />
        <main>
          <Back href="/shows" label="All shows" />
          {(error as { status?: number }).status === 404 ? <p className="empty">This show is not on GrailFind.</p> : <Failed error={error} retry={reload} />}
        </main>
      </>
    );
  if (!data)
    return (
      <>
        <Top />
        <main>
          <Back href="/shows" label="All shows" />
          <Spinner />
        </main>
      </>
    );

  const base = `/s/${encodeURIComponent(data.show.slug)}`;
  const tableHref = (label: string) => `${base}?${new URLSearchParams({ table: label })}#plan`;
  const onPlan = (label: string | null) => !!label && !!data.plan && data.tables.some((t) => t.label.toLowerCase() === label.trim().toLowerCase());

  return (
    <>
      <Top />
      <main>
        <Back href="/shows" label="All shows" />
        <h1>{data.show.name}</h1>
        <p className="lede">{data.show.when}</p>
        {data.show.hours ? <p className="lede">{data.show.hours}</p> : null}
        <StockCheck checkedAt={data.checked_at} refresh={reload} loading={loading} error={error} />
        <Plan data={data} tableHref={tableHref} />
        <h2>Shops</h2>
        {data.vendors.length === 0 ? (
          <p className="empty">No shops list their cards for this show yet. Check again closer to the day.</p>
        ) : (
          <div className="grp">
            {data.vendors.map((v, i) => (
              <div className="vend" key={`${v.shop}-${i}`}>
                {v.table_label ? (
                  onPlan(v.table_label) ? (
                    <Link className="tbl" href={tableHref(v.table_label)}>
                      {v.table_label}
                    </Link>
                  ) : (
                    <span className="tbl" aria-label={`Table ${v.table_label}`}>
                      {v.table_label}
                    </span>
                  )
                ) : (
                  <span className="tbl none">No table yet</span>
                )}
                <b>{v.shop}</b>
                <span className="n">{cards(v.cards)}</span>
              </div>
            ))}
          </div>
        )}
        <h2>Cards</h2>
        <CardFilters key={qs} base={base} data={data} />
        <Catalogue data={data} base={base} tableHref={tableHref} onPlan={onPlan} />
      </main>
    </>
  );
}

function Plan({ data, tableHref }: { data: ShowAnswer; tableHref: (label: string) => string }) {
  if (!data.plan) return null;
  const lit = data.lit ? data.tables.find((t) => t.label === data.lit) : undefined;
  return (
    <>
      <h2>Floor plan</h2>
      <div className="plan" id="plan">
        <img src={apiUrl(data.plan.url)} alt={`Floor plan of ${data.show.name}`} />
        {data.tables.map((t) => {
          const isLit = lit?.id === t.id;
          const style = { left: `${t.across.toFixed(1)}%`, top: `${t.down.toFixed(1)}%` };
          return t.shops.length ? (
            <Link
              key={t.id}
              className={`pin on${isLit ? ' lit' : ''}`}
              style={style}
              href={tableHref(t.label)}
              aria-label={`Table ${t.label}`}
              aria-current={isLit ? 'location' : undefined}>
              <span>{t.label}</span>
            </Link>
          ) : (
            <span key={t.id} className={`pin${isLit ? ' lit' : ''}`} style={style} aria-label={`Table ${t.label}`}>
              <span>{t.label}</span>
            </span>
          );
        })}
      </div>
      {lit ? (
        <p className="plan-at" role="status">
          Table {lit.label}
          {lit.shops.length ? `: ${lit.shops.join(', ')}` : ''}
        </p>
      ) : (
        <p className="lede">Tap a shop's table to find it on the plan.</p>
      )}
    </>
  );
}

function CardFilters({ base, data }: { base: string; data: ShowAnswer }) {
  const [f, setF] = useState<Filters>(data.filters);
  const set = (k: keyof Filters, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate(`${base}${query(f, 1)}`, { replace: true });
  };
  return (
    <form className="filters" onSubmit={submit}>
      <label className="wide">
        Search cards
        <input type="search" value={f.q ?? ''} onChange={(e) => set('q', e.target.value)} placeholder="Charizard, 151, 4/102" autoComplete="off" />
      </label>
      <label className="check wide">
        <input type="checkbox" checked={f.slabs} onChange={(e) => set('slabs', e.target.checked)} />
        Slabs only
      </label>
      <label>
        Grader
        <select value={f.grader ?? ''} onChange={(e) => set('grader', e.target.value)}>
          <option value="">Any grader</option>
          {data.graders.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </label>
      <label>
        Grade
        <input type="text" value={f.grade ?? ''} onChange={(e) => set('grade', e.target.value)} placeholder="10" inputMode="decimal" autoComplete="off" />
      </label>
      <label>
        Language
        <select value={f.lang ?? ''} onChange={(e) => set('lang', e.target.value)}>
          <option value="">Any language</option>
          {data.languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Set
        <select value={f.set ?? ''} onChange={(e) => set('set', e.target.value)}>
          <option value="">Any set</option>
          {data.sets.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label>
        Lowest price
        <input type="text" value={f.min ?? ''} onChange={(e) => set('min', e.target.value)} placeholder="0" inputMode="decimal" autoComplete="off" />
      </label>
      <label>
        Highest price
        <input type="text" value={f.max ?? ''} onChange={(e) => set('max', e.target.value)} placeholder="Any" inputMode="decimal" autoComplete="off" />
      </label>
      <div className="acts">
        <button className="go" type="submit">
          Show cards
        </button>
        <Link className="clear" href={base}>
          Clear
        </Link>
      </div>
    </form>
  );
}

/** The search and filters as an address's query, for page `page` ("" when nothing is set on page 1). */
export function query(f: Filters, page: number): string {
  const q = new URLSearchParams();
  for (const k of FILTER_KEYS) {
    const v = f[k];
    if (k === 'slabs') {
      if (v) q.set('slabs', '1');
    } else if (typeof v === 'string' && v.trim() !== '') q.set(k, v.trim());
  }
  if (page > 1) q.set('page', String(page));
  const s = q.toString();
  return s ? `?${s}` : '';
}

function Catalogue({
  data,
  base,
  tableHref,
  onPlan,
}: {
  data: ShowAnswer;
  base: string;
  tableHref: (label: string) => string;
  onPlan: (label: string | null) => boolean;
}) {
  if (data.total === 0)
    return (
      <p className="empty">
        {data.filtered
          ? 'No cards match. Try fewer filters, or clear them.'
          : data.vendors.length === 0
            ? 'No cards listed for this show yet.'
            : 'The shops at this show have no cards listed right now.'}
      </p>
    );
  return (
    <>
      <p className="sum">{data.filtered ? `${cards(data.total)} match` : `${cards(data.total)} at this show`}</p>
      <div className="row">
        {data.cards.map((c) => (
          <Pocket key={c.id} card={c} tableHref={tableHref} onPlan={onPlan} />
        ))}
      </div>
      {data.pages > 1 ? (
        <nav className="pager" aria-label="Pages">
          {data.page > 1 ? <Link href={`${base}${query(data.filters, data.page - 1)}`}>Previous</Link> : <span className="off" aria-hidden="true">Previous</span>}
          <span className="at">
            Page {data.page} of {data.pages}
          </span>
          {data.page < data.pages ? <Link href={`${base}${query(data.filters, data.page + 1)}`}>Next</Link> : <span className="off" aria-hidden="true">Next</span>}
        </nav>
      ) : null}
    </>
  );
}

function Pocket({ card, tableHref, onPlan }: { card: CatalogueCard; tableHref: (label: string) => string; onPlan: (label: string | null) => boolean }) {
  const facts = [card.edition, card.grade, card.set].filter((x): x is string => !!x).join(' · ');
  const at = [card.table_label && `Table ${card.table_label}`, card.shop].filter(Boolean).join(' · ');
  return (
    <div className="pocket">
      <Thumb id={card.id} name={card.name} picture={card.picture} slab={card.slab} />
      <span className="nm">{card.name}</span>
      <span className="gd">{availabilityText(card.availability)}</span>
      {facts ? <span className="gd">{facts}</span> : null}
      {card.price ? <span className="pr">{card.price}</span> : <span className="pr ask">Ask the seller</span>}
      {card.table_label && onPlan(card.table_label) ? (
        <Link className="vd" href={tableHref(card.table_label)}>
          {at}
        </Link>
      ) : (
        <span className="vd">{at}</span>
      )}
    </div>
  );
}
